/**
 * @file HeroSection.tsx
 * @description Landing hero section for the Ultimind home page.
 */

import { logoBase64 } from '@/assets/logo-base64'
import logoFallbackUrl from '@/assets/logoFallback'

const LOGO_URL = logoBase64

/**
 * HeroSection component introduces Ultimind and its core value proposition.
 */
export function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-slate-950 via-slate-950 to-slate-900">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(56,189,248,0.20),_transparent_55%)]" />
      <div className="relative mx-auto flex max-w-6xl flex-col items-start gap-10 px-4 pb-20 pt-10 md:flex-row md:items-center md:pb-24 md:pt-16 md:px-6">
        <div className="flex-1">
          <div className="inline-flex items-center gap-3 rounded-full border border-sky-500/30 bg-slate-900/70 px-3 py-1 text-xs font-medium text-sky-300">
            <img
              src={LOGO_URL}
              alt="Ultimind symbol"
              className="h-5 w-5 rounded-full object-contain"
              referrerPolicy="no-referrer"
            />
            <span>Agentic AI · Multi-Agent Orchestration</span>
          </div>
          <h1 className="mt-6 text-3xl font-semibold tracking-tight text-slate-50 sm:text-4xl md:text-5xl">
            Turn enterprise workflows into{' '}
            <span className="bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
              intelligent agent teams
            </span>
            .
          </h1>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base">
            Ultimind is a Singapore-headquartered technology company with an
            R&amp;D center in Hangzhou. We build enterprise-grade Agentic AI
            solutions that transform operations, marketing, and digital
            assistants into coordinated multi-agent systems.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-4">
            <a
              href="#/contact"
              className="rounded-full bg-gradient-to-r from-sky-500 to-blue-600 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-sky-500/40 transition hover:from-sky-400 hover:to-blue-500"
            >
              Schedule a demo
            </a>
            <a
              href="#/solutions"
              className="text-sm font-semibold text-sky-300 hover:text-sky-200"
            >
              Explore enterprise solutions →
            </a>
          </div>
          <dl className="mt-8 grid grid-cols-2 gap-4 text-xs text-slate-300 sm:text-sm md:max-w-md">
            <div>
              <dt className="font-semibold text-slate-200">
                Multi-Agent orchestration
              </dt>
              <dd className="mt-1">
                Planner, researcher, coder, and operator agents collaborate as a
                digital team.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-slate-200">
                Enterprise-grade governance
              </dt>
              <dd className="mt-1">
                Full audit trail, policy control, and safe tool execution.
              </dd>
            </div>
          </dl>
        </div>
        <div className="flex-1">
          <div className="relative mx-auto max-w-md rounded-3xl border border-sky-500/30 bg-slate-900/70 p-5 shadow-[0_0_60px_rgba(56,189,248,0.35)]">
            <div className="mb-4 flex items-center justify-between text-xs text-slate-300">
              <span className="font-semibold text-sky-300">
                Ultimind Multi-Agent Canvas
              </span>
              <span>Live orchestration</span>
            </div>
            <div className="grid gap-3 text-xs text-slate-200">
              <div className="flex items-center justify-between rounded-2xl bg-slate-800/70 px-3 py-2">
                <span className="font-medium text-sky-200">Coordinator</span>
                <span className="text-[10px] text-emerald-400">
                  Routing requests
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-2xl bg-slate-800/70 p-2">
                  <p className="text-[11px] font-semibold text-sky-200">
                    Planner
                  </p>
                  <p className="mt-1 text-[10px] text-slate-300">
                    Breaks down goals into tasks and dependencies.
                  </p>
                </div>
                <div className="rounded-2xl bg-slate-800/70 p-2">
                  <p className="text-[11px] font-semibold text-sky-200">
                    Researcher
                  </p>
                  <p className="mt-1 text-[10px] text-slate-300">
                    Uses RAG to gather verified context.
                  </p>
                </div>
                <div className="rounded-2xl bg-slate-800/70 p-2">
                  <p className="text-[11px] font-semibold text-sky-200">
                    Coder
                  </p>
                  <p className="mt-1 text-[10px] text-slate-300">
                    Executes calculations and validations.
                  </p>
                </div>
              </div>
              <div className="rounded-2xl bg-slate-800/70 p-3">
                <p className="text-[11px] font-semibold text-sky-200">
                  Reporter
                </p>
                <p className="mt-1 text-[10px] text-slate-300">
                  Generates structured reports for KYC, operations, or
                  marketing—with citations and full traceability.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
